import React, { useState, useRef, useEffect } from "react";
import { ArrowRight, ArrowLeft, Bookmark, Lock, Check, Upload, PartyPopper, Loader2, Copy, FileCheck2 } from "lucide-react";
import { loadSchema } from "../formSchema";
import { useToast } from "../hooks/use-toast";
import api from "../api";

const CHUNK = 5 * 1024 * 1024; // 5MB
const TOKEN_KEY = "hss_submission_token";

const getSubmissionToken = () => {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get("resume");
  if (fromUrl) {
    localStorage.setItem(TOKEN_KEY, fromUrl);
    return fromUrl;
  }
  let t = localStorage.getItem(TOKEN_KEY);
  if (!t) {
    t = (crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36)).replace(/-/g, "");
    localStorage.setItem(TOKEN_KEY, t);
  }
  return t;
};

const CharCount = ({ value, max }) => (
  <div className="text-right text-[0.72rem] mt-1" style={{ color: (value || "").length > max ? "#f87171" : "#6f6a80" }}>
    {(value || "").length}/{max}
  </div>
);

const Chip = ({ label, active, onClick }) => (
  <button type="button" onClick={onClick}
    className="flex items-center gap-2 rounded-full px-3.5 py-2 text-[0.86rem] font-ui font-medium transition-all duration-200 text-left"
    style={{
      background: active ? "rgba(140,255,0,0.12)" : "rgba(255,255,255,0.03)",
      border: `1px solid ${active ? "rgba(140,255,0,0.6)" : "rgba(255,255,255,0.09)"}`,
      color: active ? "#d8ffab" : "#b9b5c6",
    }}>
    <span className="flex items-center justify-center rounded-md shrink-0"
      style={{ width: 17, height: 17, background: active ? "#8cff00" : "transparent", border: `1px solid ${active ? "#8cff00" : "rgba(255,255,255,0.22)"}` }}>
      {active && <Check size={12} strokeWidth={3} color="#0a0a0a" />}
    </span>
    {label}
  </button>
);

export const Field = ({ field, data, onChange, onToggle, onFile, error, uploads }) => {
  const value = data[field.name];
  const up = uploads[field.name];
  const common = {
    id: field.name, name: field.name, value: value || "", onChange,
    placeholder: field.placeholder || "", maxLength: field.maxLength,
    className: `hss-input ${error ? "!border-red-400/70" : ""}`,
  };

  const control = () => {
    if (field.type === "textarea") return (<><textarea rows={4} {...common} />{field.maxLength && <CharCount value={value} max={field.maxLength} />}</>);
    if (field.type === "select") return (
      <select {...common}><option value="" disabled>Select an option{"\u2026"}</option>
        {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
    if (field.type === "file") return (
      <div>
        <label className={`hss-input flex items-center gap-3 cursor-pointer ${value ? "text-white" : "text-[#7d7890]"} hover:text-white`}>
          {up?.busy ? <Loader2 size={18} className="animate-spin shrink-0" style={{ color: "#8cff00" }} /> : value ? <FileCheck2 size={18} className="shrink-0" style={{ color: "#8cff00" }} /> : <Upload size={18} className="shrink-0" />}
          <span className="truncate">{up?.busy ? `Uploading\u2026 ${up.progress}%` : value ? value : "Choose a file to upload\u2026"}</span>
          <input type="file" className="hidden" disabled={up?.busy} onChange={(e) => e.target.files?.[0] && onFile(field.name, e.target.files[0])} />
        </label>
        {up?.busy && <div className="progress-track mt-2" style={{ height: 5 }}><div className="progress-fill" style={{ width: `${up.progress}%` }} /></div>}
      </div>
    );
    if (field.type === "checkboxes") {
      const selected = Array.isArray(value) ? value : [];
      const otherActive = selected.includes("Other");
      return (
        <>
          <div className="flex flex-wrap gap-2.5 mt-2">
            {field.options.map((opt) => <Chip key={opt} label={opt} active={selected.includes(opt)} onClick={() => onToggle(field.name, opt)} />)}
          </div>
          {otherActive && (
            <input type="text" placeholder="Please specify…" value={data[`${field.name}Other`] || ""}
              onChange={(e) => onChange({ target: { name: `${field.name}Other`, value: e.target.value } })} className="hss-input mt-3" />
          )}
        </>
      );
    }
    return (<><input type={field.type} {...common} />{field.maxLength && <CharCount value={value} max={field.maxLength} />}</>);
  };

  return (
    <div className={field.full ? "md:col-span-2" : ""}>
      <label htmlFor={field.name} className="font-ui font-semibold text-white text-[0.96rem] block">
        {field.label}{field.required && <span style={{ color: "#8cff00" }}> *</span>}
      </label>
      {field.helper && <p className="text-[#7d7890] text-[0.82rem] mt-0.5 mb-2">{field.helper}</p>}
      <div className={field.helper ? "" : "mt-2"}>{control()}</div>
    </div>
  );
};

const ArtistForm = () => {
  const { toast } = useToast();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});
  const [errors, setErrors] = useState({});
  const [uploads, setUploads] = useState({});
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resumeLink, setResumeLink] = useState("");
  const tokenRef = useRef(getSubmissionToken());
  const topRef = useRef(null);
  const [sections, setSections] = useState(null);

  const TOTAL = sections?.length || 0;
  const section = sections?.[step];
  const percent = TOTAL ? Math.round(((step + 1) / TOTAL) * 100) : 0;

  useEffect(() => {
    (async () => {
      try {
        const schema = await loadSchema();
        setSections(schema);
        const token = tokenRef.current;
        const { data: sub } = await api.get(`/public/submissions/${token}`);
        setResumeLink(`${window.location.origin}/first-season-artist-info?resume=${token}`);
        if (sub) {
          if (sub.status === "submitted") { setDone(true); }
          setData(sub.data || {});
          setStep(Math.min(sub.step || 0, schema.length - 1));
        }
      } catch (e) {} finally { setLoading(false); }
    })();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((d) => ({ ...d, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: false }));
  };

  const handleToggle = (name, opt) => {
    setData((d) => {
      const cur = Array.isArray(d[name]) ? d[name] : [];
      const next = cur.includes(opt) ? cur.filter((o) => o !== opt) : [...cur, opt];
      return { ...d, [name]: next };
    });
    if (errors[name]) setErrors((er) => ({ ...er, [name]: false }));
  };

  const handleFile = async (field, file) => {
    setUploads((u) => ({ ...u, [field]: { busy: true, progress: 0 } }));
    try {
      const token = tokenRef.current;
      const initFd = new FormData();
      initFd.append("filename", file.name); initFd.append("content_type", file.type || ""); initFd.append("field", field); initFd.append("submission_token", token);
      const { data: init } = await api.post("/public/uploads/init", initFd);
      const total = Math.max(1, Math.ceil(file.size / CHUNK));
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
        const fd = new FormData();
        fd.append("index", i); fd.append("chunk", blob, file.name);
        await api.post(`/public/uploads/chunk/${init.upload_id}`, fd);
        setUploads((u) => ({ ...u, [field]: { busy: true, progress: Math.round(((i + 1) / total) * 100) } }));
      }
      const cf = new FormData();
      cf.append("filename", file.name); cf.append("content_type", file.type || ""); cf.append("field", field); cf.append("submission_token", token);
      const { data: doneUp } = await api.post(`/public/uploads/complete/${init.upload_id}`, cf);
      setData((d) => ({ ...d, [field]: doneUp.filename, [`${field}__id`]: doneUp.file_id }));
      setUploads((u) => ({ ...u, [field]: { busy: false, progress: 100 } }));
      if (errors[field]) setErrors((er) => ({ ...er, [field]: false }));
      toast({ title: "File uploaded", description: `${doneUp.filename} was uploaded securely.` });
    } catch (e) {
      setUploads((u) => ({ ...u, [field]: { busy: false, progress: 0 } }));
      toast({ title: "Upload failed", description: "Please try uploading that file again." });
    }
  };

  const validate = () => {
    // Required fields are visual-only (marked with *) per product choice; never block navigation/submit.
    setErrors({});
    return true;
  };

  const scrollToTop = () => topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const persistDraft = async (nextStep) => {
    try {
      await api.put("/public/draft", { submission_token: tokenRef.current, data, step: nextStep });
    } catch (e) {}
  };

  const handleContinue = async () => {
    if (!validate()) { toast({ title: "A few fields need your attention", description: "Please complete the required fields marked with *." }); return; }
    if (step < TOTAL - 1) {
      const next = step + 1;
      setStep(next); setErrors({}); setTimeout(scrollToTop, 60);
      persistDraft(next);
    } else {
      setSubmitting(true);
      try {
        await api.post("/public/submit", { submission_token: tokenRef.current, data });
        localStorage.removeItem(TOKEN_KEY);
        setDone(true); setTimeout(scrollToTop, 60);
      } catch (e) {
        toast({ title: "Submission failed", description: "Please try again in a moment." });
      } finally { setSubmitting(false); }
    }
  };

  const handleBack = () => { if (step > 0) { setStep((s) => s - 1); setErrors({}); setTimeout(scrollToTop, 60); } };

  const handleSaveLater = async () => {
    setSaving(true);
    try {
      await api.put("/public/draft", { submission_token: tokenRef.current, data, step });
      const link = `${window.location.origin}/first-season-artist-info?resume=${tokenRef.current}`;
      setResumeLink(link);
      toast({ title: "Progress saved", description: "Use your private return link to finish later." });
    } catch (e) { toast({ title: "Could not save", description: "Please try again." }); }
    finally { setSaving(false); }
  };

  const copyLink = () => { navigator.clipboard?.writeText(resumeLink); toast({ title: "Link copied", description: "Your private return link is on your clipboard." }); };

  if (loading) return (<div className="flex justify-center py-10"><Loader2 className="animate-spin" style={{ color: "#8cff00" }} size={30} /></div>);

  if (done) return (
    <div ref={topRef} className="max-w-3xl mx-auto px-5 sm:px-8 pb-8">
      <div className="hss-card p-10 sm:p-14 text-center fade-up">
        <div className="inline-flex items-center justify-center rounded-full mb-6" style={{ width: 72, height: 72, background: "rgba(140,255,0,0.12)", border: "1px solid rgba(140,255,0,0.4)" }}>
          <PartyPopper size={32} style={{ color: "#8cff00" }} />
        </div>
        <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl">You're all set.</h2>
        <p className="font-serif-body text-white/60 mt-4 text-[1.05rem] leading-relaxed">
          Thank you for sharing your story with HSS Feed. Our team will review your responses and reach out
          if we need anything for the final cut. Welcome to First Season.
        </p>
        <div className="flex items-center justify-center gap-2 mt-8 text-white/40 text-sm">
          <Lock size={15} /><span>Your answers and files were sent securely and are not displayed publicly.</span>
        </div>
      </div>
    </div>
  );

  if (!section) return (<div className="flex justify-center py-10"><Loader2 className="animate-spin" style={{ color: "#8cff00" }} size={30} /></div>);

  return (
    <div ref={topRef} className="max-w-5xl mx-auto px-5 sm:px-8">
      <div className="mb-6">
        <p className="kicker" style={{ color: "#d8b4fe" }}>Section {step + 1} of {TOTAL}</p>
        <div className="flex items-end justify-between gap-4 mt-3">
          <h2 className="font-ui font-extrabold uppercase text-white text-[1.9rem] sm:text-[2.3rem] leading-none">{section.title}</h2>
          <span className="font-ui font-bold text-[1.05rem]" style={{ color: "#8cff00" }}>{percent}%</span>
        </div>
        <div className="progress-track mt-4"><div className="progress-fill" style={{ width: `${percent}%` }} /></div>
      </div>

      <div className="hss-card p-6 sm:p-9" key={step}>
        <div className="fade-up">
          <p className="text-white/55 text-[0.95rem] mb-7">{section.intro}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
            {section.fields.map((f) => (
              <Field key={f.name} field={f} data={data} onChange={handleChange} onToggle={handleToggle} onFile={handleFile} error={errors[f.name]} uploads={uploads} />
            ))}
          </div>
          <div className="flex items-center justify-between mt-9">
            {step > 0 ? (
              <button onClick={handleBack} className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors font-ui font-semibold text-sm uppercase tracking-wider">
                <ArrowLeft size={17} /> Back
              </button>
            ) : (<span />)}
            <button onClick={handleContinue} disabled={submitting} className="btn-green">
              {submitting ? <Loader2 size={17} className="animate-spin" /> : null}
              {step === TOTAL - 1 ? "Submit" : "Continue"}
              {!submitting && (step === TOTAL - 1 ? <Check size={17} /> : <ArrowRight size={17} />)}
            </button>
          </div>
        </div>
      </div>

      <div className="hss-card p-6 sm:p-7 mt-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div>
            <h4 className="font-ui font-bold text-white text-[1.02rem]">Need more time?</h4>
            <p className="text-white/55 text-[0.9rem] mt-1">Save your answers and selected files, then use a private return link to finish later.</p>
          </div>
          <button onClick={handleSaveLater} disabled={saving} className="btn-green-outline whitespace-nowrap">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Bookmark size={16} />} Save &amp; finish later
          </button>
        </div>
        {resumeLink && (
          <div className="mt-4 flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5">
            <input readOnly value={resumeLink} className="flex-1 bg-transparent text-white/70 text-sm outline-none truncate" />
            <button onClick={copyLink} className="flex items-center gap-1.5 text-sm font-semibold shrink-0" style={{ color: "#8cff00" }}><Copy size={14} /> Copy</button>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-2 mt-6 text-white/40 text-[0.85rem]">
        <Lock size={14} /><span>Your answers and files are sent securely to HSS Feed and are not displayed publicly.</span>
      </div>
    </div>
  );
};

export default ArtistForm;
